import { CARD_SOURCE_KINDS, ItemV1, SOURCE_KINDS, SpellV1, STATBLOCK_SCHEMA_VERSION, StatBlockV1, } from '@codex/dnd5e/schema';
import { DerivedSource, ReferenceKind } from '../domain/enums.js';
import { z } from '../zod.js';
import { IsoTimestamp, RowVersion, Uuid } from './common.js';
import { DocMetaV1, DocTitle, DocumentV1, JsonObject } from './documents.js';
/**
 * The 5e shapes of exit 1b (1.0b deliverable 20): actor sheets (`StatBlockV1` data; 1.AA, then
 * 1.AC-1), spell and item cards (`rules_record`), the SRD reference entry (1.AA) and the variant
 * flow (1.AC-1). The 2014 schemas are `@codex/dnd5e/schema`'s, the one workspace package contracts
 * imports; `actor_sheet.source` and `rules_record.source` are its `SOURCE_KINDS` and
 * `CARD_SOURCE_KINDS`.
 */
const Sha256 = z.string().regex(/^[0-9a-f]{64}$/);
const SheetLabel = z.string().min(1).max(200);
/** Where a sheet path's value came from (`actor_sheet.field_origins`, path to origin). */
export const FIELD_ORIGINS = ['foundry', 'local'];
/**
 * A stat block sheet (`actor_sheet` of kind `statblock`; a character sheet is 2.0b's), its data a
 * `StatBlockV1` and `derived` what 1.AA's deriver computed from it (null until it ran); `version`
 * guards every write. Foundry's import columns arrive with Phase 2c.
 */
export const ActorSheetV1 = z.strictObject({
    id: Uuid,
    documentId: Uuid,
    label: SheetLabel.nullable(),
    isPrimary: z.boolean(),
    kind: z.literal('statblock'),
    rulesVersion: z.literal('2014'),
    schemaVersion: z.literal(STATBLOCK_SCHEMA_VERSION),
    data: StatBlockV1,
    derived: JsonObject.nullable(),
    derivedSource: DerivedSource,
    source: z.enum(SOURCE_KINDS),
    fieldOrigins: z.record(z.string().min(1).max(500), z.enum(FIELD_ORIGINS)),
    version: RowVersion,
    createdAt: IsoTimestamp,
    updatedAt: IsoTimestamp,
});
/** `sheets.create` on the document of the path; the first sheet becomes its primary. */
export const SheetCreateV1 = z.strictObject({
    label: SheetLabel.optional(),
    isPrimary: z.boolean().optional(),
    data: StatBlockV1,
    source: z.enum(['form', 'paste', 'ai']).optional(),
});
/** `sheets.update`: a stale `baseVersion` answers 409 `row_conflict`. */
export const SheetUpdateV1 = z.strictObject({
    baseVersion: RowVersion,
    label: SheetLabel.nullable().optional(),
    isPrimary: z.boolean().optional(),
    data: StatBlockV1.optional(),
});
/** `/api/sheets/:sheetId` and `/api/docs/:id/sheets/:sheetId`. */
export const SheetIdParams = z.strictObject({ sheetId: Uuid });
export const SheetParams = z.strictObject({ id: Uuid, sheetId: Uuid });
const CardFields = {
    documentId: Uuid,
    rv: RowVersion,
    schemaVersion: z.literal(1),
    source: z.enum(CARD_SOURCE_KINDS),
    createdAt: IsoTimestamp,
    updatedAt: IsoTimestamp,
};
/** A spell or item card (`rules_record`): `SpellV1` or `ItemV1` data by its kind. */
export const RulesRecordV1 = z.discriminatedUnion('kind', [
    z.strictObject({ kind: z.literal('spell'), data: SpellV1, ...CardFields }),
    z.strictObject({ kind: z.literal('item'), data: ItemV1, ...CardFields }),
]);
/** The source a reference entry was seeded from (`reference_source`), with its licence. */
export const ReferenceSourceV1 = z.strictObject({
    key: z.string().min(1).max(100),
    title: z.string().min(1).max(500),
    licence: z.string().min(1).max(200),
    attribution: z.string().min(1).max(2000),
});
/**
 * A reference page (A2.1 section 4): the read-only document, its `reference_entry` and source,
 * and its rules: a creature's primary sheet, or a spell's or item's card.
 */
export const ReferenceEntryV1 = z
    .strictObject({
    document: DocumentV1,
    kind: ReferenceKind,
    source: ReferenceSourceV1,
    upstreamKey: z.string().min(1).max(200),
    contentHash: Sha256,
    withdrawnAt: IsoTimestamp.nullable(),
    sheet: ActorSheetV1.nullable(),
    card: RulesRecordV1.nullable(),
})
    .refine((e) => e.kind === 'creature'
    ? e.sheet !== null && e.card === null
    : e.sheet === null && e.card?.kind === e.kind, {
    message: 'a creature has its sheet only; a spell or an item its card of the same kind only',
    path: ['sheet'],
});
/** The kinds of a card (`rules_record.kind`), each the key of a canon type with `has_card`. */
export const CARD_KINDS = ['spell', 'item'];
/**
 * Where a card written through `rules.create` came from (CS r6c deliverable 202): the GM's form, or
 * a paste. The server stores it as the card's `data.source` (2.M's `cardSource`), whatever the
 * request's data holds there.
 */
export const CARD_WRITE_SOURCES = ['form', 'paste'];
/**
 * The `details.issue` of a card write's or a card variant's 422 `invalid_body` (2.M s1 deliverable
 * 6): the page's type has no card; the data or the type is of the other kind; `rules: '2024'`;
 * data neither schema parses; and, on `variants.create` only, an unknown or archived type.
 */
export const CARD_WRITE_ISSUES = [
    'type_without_card',
    'kind_mismatch',
    'rules_2024',
    'schema_invalid',
    'unknown_type',
];
/** A card write's 422 `details`: where the refusal points, and why. */
export const CardWriteInvalidDetailsV1 = z.strictObject({
    path: z.array(z.union([z.string().min(1).max(200), z.number().int().min(0)])).max(32),
    issue: z.enum(CARD_WRITE_ISSUES),
});
/**
 * `rules.create` (CS r6c deliverable 202; 2.M s1): the card of a canon page whose type has
 * `has_card`, of that type's kind. `data` is checked by the server against `SpellV1` or `ItemV1`
 * by `kind` (2.M's `parseCard`), so a refusal is a 422 with its `issue`, not a 400.
 */
export const RulesCreateV1 = z.strictObject({
    kind: z.enum(CARD_KINDS),
    data: JsonObject,
    source: z.enum(CARD_WRITE_SOURCES).optional(),
});
/**
 * `rules.update`: the card's new data, parsed against its stored kind; the stored `data.source`
 * is kept. A `baseRv` behind the card's `rv` answers 409 `row_conflict`.
 */
export const RulesUpdateV1 = z.strictObject({ baseRv: RowVersion, data: JsonObject });
/**
 * `variants.create` from a reference page: a canon copy of a type with `has_sheet` from a
 * creature; from a spell or an item (CS r6c, 2.M s1), a copy of the canon type with `has_card` of
 * that kind (`spell` or `item`), its card the source's, and no sheet.
 */
export const VariantCreateRequestV1 = z.strictObject({
    title: DocTitle,
    typeId: Uuid,
    homeId: Uuid.optional(),
});
/** The variant: its document, and its sheet (null for a spell's or an item's card). */
export const VariantCreatedV1 = z.strictObject({ document: DocMetaV1, sheetId: Uuid.nullable() });
//# sourceMappingURL=reference.js.map